import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/hooks'
import { useIntl } from 'react-intl'
import { v4 as uuid } from 'uuid'
import {
  Grid,
  IconButton,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@material-ui/core'
import Delete from '@material-ui/icons/Delete'
import Search from '@material-ui/icons/Search'
import { TAXES_FACTORS } from '../../../../constants'
import { CatalogueEntry } from 'models/catalogues'
import { Tax, TaxCatalogue, TaxCatalogueEntry } from 'models/invoices.models'
import { useThunkDispatch } from 'models/thunk'
import { isNumberInRange } from 'lib/helpers/ioValidations'
import { selectCatalogue, selectSelectedInvoice } from 'lib/helpers/selectors'
import { searchInCatalogue } from 'actions/catalogues'
import {
  createCreditNoteTax,
  deleteCreditNoteConcept,
  deleteCreditNoteTax,
  setCreditNoteConceptValue,
} from 'actions/invoices'
import SearchSatCodeWizardContainer from 'containers/Shared/SearchSatCodeWizardContainer/SearchSatCodeWizardContainer'
import Button from 'components/Shared/Button/Button'
import Dropdown from 'components/FormComponents/Dropdown/Dropdown'
import AutocompleteField from 'components/FormComponents/IOComponents/AutocompleteField/AutoCompleteField'
import NumericField from 'components/FormComponents/IOComponents/NumericField/NumericField'
import SearchField from 'components/FormComponents/IOComponents/SearchField'
import TextField from 'components/FormComponents/IOComponents/TextField/TextField'
import './styles.scss'

interface IAddCreditNoteConceptContainerProps {
  impuestosCat: TaxCatalogue
  uuid: string
  title: string
  clave?: CatalogueEntry | null
  unidad?: CatalogueEntry | null
  precio?: string
  cantidad?: string
  descuento?: string
  descripcion?: string
  importacion?: string
  impuestos?: Tax[]
  objImp?: CatalogueEntry | null
  predial?: string
  id: number
}

const AddCreditNoteConceptContainer = (
  props: IAddCreditNoteConceptContainerProps
) => {
  const dispatch = useDispatch()
  const thunkDispatch = useThunkDispatch()

  const intl = useIntl()

  const unidadesCat = useAppSelector(selectCatalogue('unidades'))
  const objImpCat = useAppSelector(selectCatalogue('objImp'))
  const selectedInvoice = useAppSelector(selectSelectedInvoice)

  const invoiceConcepts = selectedInvoice?.conceptos

  const [selectedTax, setSelectedTax] = useState<TaxCatalogueEntry | null>(null)
  const [rate, setRate] = useState<string>('')
  const [fee, setFee] = useState<string>('')
  const [openWizard, setOpenWizard] = useState(false)

  const addTax = () => {
    if (!selectedTax) return
    const tax: Tax = {
      ...selectedTax,
      uuid: uuid(),
      selectedValue:
        selectedTax.factor === 'Tasa' ? parseFloat(rate) : parseFloat(fee),
    }
    dispatch(createCreditNoteTax(props.uuid, tax))
    setRate('')
    setFee('')
    setSelectedTax(null)
  }

  const saveValueToStore = useCallback(
    (name: string, value: any) => {
      dispatch(setCreditNoteConceptValue(props.uuid, name, value))
    },
    [props.uuid, dispatch]
  )

  const getTaxValue = (factor: string, value: number) => {
    switch (factor) {
      case 'Tasa': {
        if (!value) return ''
        const formatted = intl.formatNumber(value, {
          style: 'percent',
          minimumFractionDigits: 2,
        })
        return formatted
      }
      case 'Cuota': {
        if (!value) return ''
        const formatted = intl.formatNumber(value, {
          style: 'currency',
          currency: 'MXN',
          currencyDisplay: 'narrowSymbol',
        })
        return formatted
      }
      default:
        return ''
    }
  }

  const handleClaveSatItems = useCallback(
    async value => {
      const data = await thunkDispatch(searchInCatalogue('items', value))
      let originConcepts = invoiceConcepts?.map(
        (concept: any) => concept.satkey.value
      )
      originConcepts?.push('84111506')
      let response = data.map(option => {
        const suggest = originConcepts?.some(
          (concept: any) => option.value === concept
        )
        return {
          suggest: suggest ? 'claves sugeridas' : '',
          ...option,
        }
      })
      response.sort((a, b) => b.suggest.localeCompare(a.suggest))
      return response
    },
    [thunkDispatch, invoiceConcepts]
  )

  const renderCatalogueOption = (o: CatalogueEntry) => (
    <span className="catalogue-option">{`${o.value} - ${o.label}`}</span>
  )

  //cargar por defecto ciertos valores de campos
  useEffect(() => {
    if (!props.clave) {
      handleClaveSatItems('84111506')
      saveValueToStore('satKey', {
        idCatalogValues: 53988,
        label: 'Servicios de facturación',
        value: '84111506',
        enabled: true,
      })
    }
    if (!props.unidad) {
      const unidad = unidadesCat.filter(m => m.value === 'ACT')[0]
      saveValueToStore('claveUnitSat', unidad)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTaxSelect = (v: any) => {
    setSelectedTax(v)
    setRate('')
    setFee('')
  }

  const isDisabledAddTaxButton = useMemo(() => {
    if (!selectedTax) return true
    const { factor } = selectedTax
    if (factor === TAXES_FACTORS.exent) return false
    if (factor === TAXES_FACTORS.rate && rate !== '') return false
    if (factor === TAXES_FACTORS.fee && fee !== '') return false
    return true
  }, [selectedTax, rate, fee])

  return (
    <Dropdown
      id={`conceptDropdownBtn_${props.id}`}
      title={props.title}
      className="add-invoice-product-form"
      capitalize
      withDivider
      defaultExpanded>
      <Grid container spacing={4} className="basic-information">
        <Grid item xs={12} className="item-actions">
          <Button
            id={`deleteConceptBtn_${props.id}`}
            startIcon={<Delete />}
            onClick={() => dispatch(deleteCreditNoteConcept(props.uuid))}>
            Eliminar Concepto
          </Button>
        </Grid>
        <Grid item xs={4}>
          <SearchField
            name={`claveSat_${props.id}`}
            label="Clave SAT"
            onSearch={(v: string) => handleClaveSatItems(v)}
            groupBy={(option: any) => option.suggest}
            value={props.clave}
            onValue={(v: CatalogueEntry) => saveValueToStore('satKey', v)}
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            getOptionSelected={(
              option: CatalogueEntry,
              value: CatalogueEntry
            ) => option.idCatalogValues === value.idCatalogValues}
            renderOption={(o: any) => renderCatalogueOption(o)}
            InputProps={{
              endAdornment: (
                <IconButton onClick={() => setOpenWizard(true)} size="small">
                  <Search />
                </IconButton>
              ),
            }}
            fullWidth
            required
            exclude
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteField
            name={`unidad_${props.id}`}
            label="Unidad o medida"
            catalogue={unidadesCat}
            value={props.unidad}
            onValue={(v: CatalogueEntry) => saveValueToStore('claveUnitSat', v)}
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            renderOption={(o: any) => renderCatalogueOption(o)}
            getOptionSelected={(
              option: CatalogueEntry,
              value: CatalogueEntry
            ) => option.idCatalogValues === value.idCatalogValues}
            fullWidth
            required
            exclude
          />
        </Grid>
        <Grid item xs={4}>
          <AutocompleteField
            name={`objImp_${props.id}`}
            label="Objeto de Impuesto"
            catalogue={objImpCat}
            value={props.objImp}
            onValue={(v: CatalogueEntry) => saveValueToStore('objImp', v)}
            getOptionLabel={(o: any) => `${o.value} - ${o.label}`}
            renderOption={(o: any) => renderCatalogueOption(o)}
            getOptionSelected={(
              option: CatalogueEntry,
              value: CatalogueEntry
            ) => option.idCatalogValues === value.idCatalogValues}
            fullWidth
            required
          />
        </Grid>
        <Grid item xs={4}>
          <NumericField
            name={`precioUnitario_${props.id}`}
            label="Precio unitario"
            value={props.precio}
            onValue={(v: string) => saveValueToStore('price', v)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            fullWidth
            required
            exclude
          />
        </Grid>
        <Grid item xs={4}>
          <NumericField
            name={`cantidad_${props.id}`}
            label="Cantidad"
            value={props.cantidad}
            onValue={(v: string) => saveValueToStore('cantidad', v)}
            fullWidth
            required
            exclude
          />
        </Grid>
        <Grid item xs={4}>
          <NumericField
            name={`descuento_${props.id}`}
            label="Descuento"
            value={props.descuento}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">$</InputAdornment>
              ),
            }}
            onValue={(v: string) => saveValueToStore('descuento', v)}
            fullWidth
            exclude
          />
        </Grid>
        <Grid item xs={8}>
          <p className="form-label">Descripción</p>
          <TextField
            variant="outlined"
            name={`description_${props.id}`}
            value={props.descripcion}
            onValue={(v: string) => saveValueToStore('description', v)}
            inputProps={{ maxLength: 500 }}
            multiline
            minRows={4}
            maxRows={4}
            withCounter
            fullWidth
            required
            exclude
          />
        </Grid>
      </Grid>
      <Dropdown
        id={`impuestosDropdown_${props.id}`}
        title="Impuestos"
        headerBackgroundColor="gray"
        bordered>
        <Grid container spacing={4}>
          <Grid item xs={4}>
            <AutocompleteField
              name={`impuesto_${props.id}`}
              label="Impuesto"
              catalogue={props.impuestosCat}
              value={selectedTax}
              onValue={handleTaxSelect}
              getOptionLabel={(o: any) =>
                `${String(o.id).padStart(2, '0')} - ${o.label}`
              }
              fullWidth
              exclude
            />
          </Grid>
          {!!selectedTax && Array.isArray(selectedTax?.value) && (
            <Grid item xs={4}>
              <AutocompleteField
                name={`tasa_${props.id}`}
                label="Tasa (%)"
                catalogue={selectedTax?.value.map(v => ({
                  label: `${v * 100}%`,
                  value: v,
                }))}
                value={{ label: `${Number(rate) * 100}%`, value: rate }}
                onValue={(v: any) => setRate(v.value)}
                getOptionLabel={(o: any) => o.label}
                inputProps={{ readOnly: true }}
                fullWidth
                exclude
              />
            </Grid>
          )}
          {!!selectedTax && !Array.isArray(selectedTax?.value) && (
            <Grid item xs={4}>
              <NumericField
                name={`cuota_${props.id}`}
                label="Cuota ($)"
                value={fee}
                onValue={(v: string) => setFee(v)}
                validate={(v: string) => {
                  const minOperand =
                    selectedTax?.value &&
                    typeof selectedTax.value === 'object' &&
                    'min' in selectedTax.value
                      ? selectedTax.value.min
                      : 0
                  const maxOperand =
                    selectedTax?.value &&
                    typeof selectedTax.value === 'object' &&
                    'max' in selectedTax.value
                      ? selectedTax.value.max
                      : 0

                  const min =
                    Number(props.cantidad) * Number(props.precio) * minOperand
                  const max =
                    Number(props.cantidad) * Number(props.precio) * maxOperand
                  return isNumberInRange(v, min, max)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                fullWidth
                exclude
              />
            </Grid>
          )}
          <Grid item xs={4}>
            <Button
              id={`addTaxBtn_${props.id}`}
              variant="outlined"
              color="warning"
              disabled={isDisabledAddTaxButton}
              onClick={addTax}>
              Agregar
            </Button>
          </Grid>
          {props.impuestos && props.impuestos.length > 0 && (
            <Grid item xs={12} className="table-taxes">
              <Table size="small">
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell
                      className="table-cell"
                      style={{ width: '150px' }}>
                      Tipo de impuesto
                    </TableCell>
                    <TableCell
                      className="table-cell"
                      style={{ width: '300px' }}>
                      Impuesto
                    </TableCell>
                    <TableCell
                      className="table-cell"
                      style={{ width: '150px' }}>
                      Tasa o cuota
                    </TableCell>
                    <TableCell className="table-cell"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {props.impuestos.map((tax, i) => (
                    <TableRow className="table-row" key={tax.uuid}>
                      <TableCell className="table-cell">{tax.type}</TableCell>
                      <TableCell className="table-cell">
                        {`
                          ${String(tax.sat?.value ?? '').padStart(3, '0')}
                          -
                          ${tax.sat?.label ?? ''}  
                        `}
                      </TableCell>
                      <TableCell className="table-cell">
                        {getTaxValue(tax.factor, tax.selectedValue)}
                      </TableCell>
                      <TableCell className="table-cell table-actions">
                        <IconButton
                          id={`deleteTaxBtn_${props.id}_${i}`}
                          onClick={() =>
                            dispatch(deleteCreditNoteTax(props.uuid, tax.uuid))
                          }
                          size="small">
                          <Delete className="delete" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Grid>
          )}
        </Grid>
      </Dropdown>
      <SearchSatCodeWizardContainer
        open={openWizard}
        onSelect={(v: CatalogueEntry) => saveValueToStore('claveSat', v)}
        onCancel={() => setOpenWizard(false)}
      />
    </Dropdown>
  )
}

export default AddCreditNoteConceptContainer
